/* eslint-disable react/no-danger */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import { Alert, IconWithText, LinkButton, Tooltip } from '@pmi/dsm-react';
import {
  AlertQuestionIcon,
  CheckIcon,
} from '@pmi/dsm-react/dist/components/icons/fa/16';
import { DetailedIcon } from '@pmi/dsm-react/dist/components/icons/tt/mark/fc';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlertInfoSolidIcon,
  AlertWarningSolidIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { PlusIcon } from '@pmi/dsm-react/dist/components/icons/16';
import ProgressBar from '../../../shared/components/ProgressBar';
import {
  extractTextFromHtml,
  toShortLocaleDateString,
} from '../../../shared/helpers';
import { Product } from '../models';
import styles from '../productcard.module.scss';
import stylesProgress from './progresscard.module.scss';
import DetailModal from '../DetailModal';
import ProductModal from '../EnrollmentCard/ProductModal';
import CategoryLabel from '../../../shared/components/CategoryLabel';
import { reportLinkClick } from '../../../shared/analytics';
import PendingMessage from './PendingMessage';
import { adminInfoSelector } from '../../../shared/store/shared/adminInfoSlice';
import { B2CPRODUCTLOCATION } from '../../../shared/utils/defaultTexts';
import CertProductStatus from '../CertProductStatus';
import {
  AddToCartItem,
  useAddToCartMutation,
} from '../EnrollmentCard/apiSlice';
import { openCart } from '../../../shared/store/shared/cartSlice';
import errorFromResponse from '../../../shared/utils/extractErrorFromResponse';
import {
  DisabledReason,
  isPayByUserCartStatus,
  PAY_BY_USER_STATUS_MAP,
  ProductTypes,
} from '../../../shared/constants';
import { isBundleOptionChapter } from '../helpers';
import { getPMISiteUrl } from '../../../shared/env-vars';

const PduTooltipContent = `Professional Development Units (PDUs) are one-hour blocks of time that you spend learning, teaching others, or volunteering.
                          By accumulating and tracking these  over a three-year period you can maintain your certification status with PMI.`;

interface ProgressCardProps {
  product: Product;
  accountId: string;
  onSuccess: (product?: Product | undefined) => void;
}

function VerticalSeparator({ className = '' }: { className?: string }) {
  return <p className={`v-sep pe-1 ps-1 my-0 ${className}`} />;
}

function PDU({ totalPDUs, level }: { totalPDUs: number; level: string }) {
  return (
    <>
      {level && (
        <div className="col-auto d-flex pe-0 ps-0 order-lg-1 align-items-center">
          <VerticalSeparator className="d-lg-none" />
          <p data-testid="courseLevelValue-testid" className="pe-0 ps-0 my-0">
            {level}
          </p>
          <VerticalSeparator />
        </div>
      )}

      {totalPDUs > 0 && (
        <div className="d-flex col-auto pe-0 ps-0 order-lg-2 align-items-center">
          <DetailedIcon color="original" fontSize="xs" className="ps-0 pe-0" />
          <p
            data-testid="pduValue-testid"
            className="label ms-1 me-1 mb-0 ps-0 pe-0"
          >
            {totalPDUs} PDUs
          </p>
          <Tooltip
            content={PduTooltipContent}
            type="sm"
            placement="bottom"
            className="ps-0 pe-0 darkTooltip"
          >
            <AlertQuestionIcon fontSize="xs" />
          </Tooltip>
        </div>
      )}
    </>
  );
}

function ProgressIndicator({ progress }: { progress: number }) {
  if (progress === null || progress === undefined) return null;
  return (
    <>
      <div className="pe-0 ps-0 col-lg-auto col-12 order-lg-3 d-flex align-items-center">
        <VerticalSeparator className="d-none d-lg-inline-block" />
        <div className={stylesProgress.progressBarContainer}>
          <ProgressBar value={progress} />
        </div>
      </div>
      <div className="col-auto ps-lg-2 pe-0 ps-0 order-lg-4 align-items-center">
        <p data-testid="progressValue-testid" className="label mb-0 ps-0 pe-0">
          {progress}% Complete
        </p>
      </div>
    </>
  );
}

function SelectedBundleOptionValues({
  bundleOptions,
}: {
  bundleOptions: Product['bundleOptions'];
}) {
  const values = (bundleOptions ?? [])
    .filter((option) => Boolean(option.selectOption))
    .map(
      (option) => option.productLinks.find((link) => link.ownsProduct)?.value,
    )
    .filter((value): value is string => Boolean(value));

  if (values.length === 0) return null;

  return (
    <>
      {values.map((value) => (
        <div
          key={value}
          className="col-auto d-flex pe-0 ps-0 align-items-center"
        >
          <VerticalSeparator className="d-lg-none" />
          <p className="label mb-0 ps-0 pe-0">{value}</p>
          <VerticalSeparator className="d-none d-lg-inline-block" />
        </div>
      ))}
    </>
  );
}

function LastViewed({ lastAccessed }: { lastAccessed: string }) {
  if (!lastAccessed) return null;
  return (
    <div className="col-auto pe-0 ps-0 d-flex order-lg-5 align-items-center">
      <VerticalSeparator className="d-md-inline-block d-none" />
      <span className="label">{`Last Viewed ${toShortLocaleDateString(
        lastAccessed,
      )}`}</span>
    </div>
  );
}

export default function ProgressCard({
  product,
  accountId,
  onSuccess,
}: ProgressCardProps) {
  const {
    name,
    shortDescription = '',
    fullDescription = '',
    type,
    level,
    totalPDUs,
    progress,
    lastAccessed,
    callToAction,
    status,
    pending,
    renewalDate,
    submitted,
    disabledReasonMessage,
    disabledReason,
    sku,
  } = product;
  const b2cProductLocation =
    B2CPRODUCTLOCATION[type as keyof typeof B2CPRODUCTLOCATION];
  const [productModalState, setProductModalState] = useState(false);
  const [detailModalState, setDetailModalState] = useState(false);
  const { adminDetails } = useSelector(adminInfoSelector);
  const [addingToCart, setAddingToCart] = useState(false);
  const dispatch = useDispatch();
  const [addToCart, addedToCart] = useAddToCartMutation();
  const isPbuProduct = () =>
    product.isPayByUserProduct &&
    isPayByUserCartStatus(product.callToAction?.text) &&
    product.price > 0;
  const onCtaClick = () => {
    if (callToAction.url) {
      window.location.href = callToAction.url;
      return;
    }
    setProductModalState(true);
  };

  const onEnrollmentSuccess = () => {
    setProductModalState(false);
    onSuccess();
  };

  const onCancel = () => {
    setProductModalState(false);
  };
  const getProductPrice = () => Number(product.price);

  const onAddToCart = () => {
    const bundleOptions = (): { optionId: number; selectionId: number }[] => {
      const options: { optionId: number; selectionId: number }[] = [];
      if (product.bundleOptions && product.bundleOptions.length > 0) {
        product.bundleOptions.map((option) => {
          if (isBundleOptionChapter(option)) {
            const ownedChapter = option.productLinks.find((l) => l.ownsProduct);
            if (ownedChapter) {
              options.push({
                optionId: option.id,
                selectionId: Number(ownedChapter.id),
              });
            }
          } else if (option.productLinks.length > 1) {
            option.productLinks.map((link) => {
              options.push({
                optionId: option.id,
                selectionId: Number(link.id),
              });
              return link;
            });
          } else if (option.productLinks.length === 1) {
            options.push({
              optionId: option.id,
              selectionId: Number(option.productLinks[0].id),
            });
          }
          return option;
        });
      }
      return options;
    };
    const item: AddToCartItem = {
      qty: 1,
      sku: product.sku,
      dealCode: product.currentDealId,
      price: getProductPrice(),
      bundleOptions: bundleOptions(),
    };
    setAddingToCart(true);
    addToCart({
      accountId,
      items: [item],
    })
      .unwrap()
      .then(() => {
        addedToCart.reset();
        dispatch(openCart());
      })
      .catch((err) => {
        if (err?.data && err?.data?.Value) {
          errorFromResponse(err?.data);
        }
      })
      .finally(() => {
        setAddingToCart(false);
      });
  };

  const isToShowPrice = () => {
    const isStillActive =
      product?.callToAction && product.callToAction?.text
        ? product.callToAction.text
            .toLowerCase()
            .includes('view membership benefits')
        : false;
    return (
      !isStillActive && product.isPayByUserProduct && getProductPrice() > 0
    );
  };

  const actionButton = () => {
    const isInCart = callToAction?.text
      .toLowerCase()
      .trim()
      .includes(PAY_BY_USER_STATUS_MAP.inCart);
    if (isPbuProduct()) {
      return (
        <LinkButton
          variant="outline-secondary"
          titleText={callToAction?.text}
          fullWidth={false}
          size="lg"
          data-testid="cta-resumecourse"
          className={styles.btnCTAction}
          icon={
            !isInCart ||
            callToAction?.text.toLowerCase().includes('add to cart')
              ? PlusIcon
              : null
          }
          disabled={addingToCart || isInCart}
          onClick={() => {
            if (isInCart || addingToCart) {
              return;
            }
            reportLinkClick(
              `${name}:${callToAction?.text}`,
              callToAction?.url,
              'b2bhub_myproducts',
            );
            onAddToCart();
          }}
        />
      );
    }
    return (
      <div className="mt-4">
        <LinkButton
          variant="outline-secondary"
          className={styles.btnCTAction}
          onClick={() => {
            reportLinkClick(
              `${name}:${callToAction?.text}`,
              callToAction?.url,
              'b2bhub_allproducts',
            );
            onCtaClick();
          }}
          titleText={callToAction?.text}
          fullWidth={false}
          data-testid="cta-button"
        />
      </div>
    );
  };

  const membershipExpiredUrl = `${getPMISiteUrl()}/dcpdp/sku/memb-b-01`;
  const isMembershipExpired =
    disabledReason === DisabledReason.MembershipExpired;
  let actionableLinkHref: string | undefined;
  let actionableLinkText: string | undefined;

  if (isMembershipExpired) {
    actionableLinkHref = membershipExpiredUrl;
    actionableLinkText = 'here.';
  } else if (disabledReason === DisabledReason.B2CPurchase) {
    actionableLinkHref = b2cProductLocation?.link;
    actionableLinkText = b2cProductLocation?.title;
  }

  return (
    <>
      <div className="d-grid col-12 mb-3">
        <div className="card-container d-grid overflow-hidden">
          <div
            className={`p-3 p-lg-4 bg-white ${styles.productCard} overflow-hidden`}
          >
            <div className="d-flex flex-column">
              <div>
                {!product?.bundleOptions.some((x) =>
                  x.title
                    ?.toLowerCase()
                    .includes(ProductTypes.membership.toLowerCase()),
                ) &&
                  product?.bundleProduct && (
                    <CategoryLabel
                      type="Bundle"
                      className="mb-3 me-2 float-start"
                    />
                  )}
                <CategoryLabel type={type} className="mb-3 float-start" />
              </div>
              <h4
                className="d-inline-block"
                dangerouslySetInnerHTML={{ __html: name }}
              />
              <div>
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      shortDescription?.length > 0
                        ? `${extractTextFromHtml(
                            shortDescription.substring(0, 133),
                          )}...`
                        : extractTextFromHtml(shortDescription),
                  }}
                />
                {(fullDescription?.length > 0 ||
                  product.shortDescription?.length > 0) && (
                  <LinkButton
                    titleText="More Details"
                    size="xs"
                    className="d-inline fw-light card-more-details text-decoration-underline"
                    onClick={() => {
                      reportLinkClick(
                        `${name}:more details`,
                        '',
                        'b2bhub_myproducts',
                      );
                      setDetailModalState(true);
                    }}
                  />
                )}
              </div>
              {disabledReasonMessage && (
                <div className="label mt-1 mt-md-3">
                  <Alert
                    variant="info"
                    alertText={disabledReasonMessage
                      .replace('%adminDetails%', adminDetails ?? '')
                      .replace(
                        '%b2cProductLocation%',
                        b2cProductLocation?.prefix ?? '',
                      )}
                    icon={AlertInfoSolidIcon}
                    actionableLinkTarget="_blank"
                    actionableLinkHref={actionableLinkHref}
                    actionableLinkText={actionableLinkText}
                  />
                </div>
              )}

              {!pending ? (
                <>
                  {type === 'Certification' && (
                    <CertProductStatus
                      accountId={accountId}
                      status={status}
                      submitted={submitted}
                    />
                  )}
                  {type === ProductTypes.membership && (
                    <div
                      className={`${stylesProgress.membershipTypeIconContainer} mt-3`}
                    >
                      {(() => {
                        const membershipText = `Your membership ${
                          isMembershipExpired ? 'has expired' : 'is active'
                        }. | ${
                          isMembershipExpired ? 'Expired on' : 'Renewal Date'
                        } ${toShortLocaleDateString(renewalDate)}`;
                        const membershipIcon = isMembershipExpired ? (
                          <AlertWarningSolidIcon fontSize="sm" color="danger" />
                        ) : (
                          <CheckIcon fontSize="sm" color="inherit" />
                        );

                        return (
                          <>
                            <IconWithText
                              icon={membershipIcon}
                              text={membershipText}
                              className="d-none d-sm-flex"
                            />
                            <div className="d-flex d-sm-none align-items-start">
                              {membershipIcon}
                              <span className="text-muted ms-1">
                                {membershipText}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {/** TODO: update SKUs for Study Hall as needed */}
                  {type === 'Digital Product' &&
                    (sku === 'DP015' || sku === 'DP017') && (
                      <div
                        className={`${stylesProgress.membershipTypeIconContainer} mt-3`}
                      >
                        <IconWithText
                          icon={<CheckIcon fontSize="sm" color="inherit" />}
                          text={`Your account is active. ${
                            renewalDate
                              ? `| Valid Through: ${toShortLocaleDateString(
                                  renewalDate,
                                )}`
                              : ''
                          }`}
                        />
                      </div>
                    )}

                  {/** TODO: update SKUs for Study Hall as needed */}
                  {(type === 'Digital Product' || type === 'eLearning') &&
                    sku !== 'DP015' &&
                    sku !== 'DP017' && (
                      <div className="row my-2 label ms-0 gy-1">
                        <ProgressIndicator progress={progress} />
                        <SelectedBundleOptionValues
                          bundleOptions={product.bundleOptions}
                        />
                        <PDU totalPDUs={totalPDUs} level={level} />
                        <LastViewed lastAccessed={lastAccessed} />
                      </div>
                    )}
                  {isToShowPrice() && type === ProductTypes.membership && (
                    <>
                      <hr className={styles.hSep} />
                      <h5 className={`${styles.payByUserPrice} mb-0`}>
                        <span>Renewal price</span>
                        {product.currencySymbol}
                        {getProductPrice().toFixed(2)}
                        {getProductPrice() < product.msrpPrice &&
                          product.msrpPrice > 0 && (
                            <span
                              className="msrpPrice"
                              style={{ textDecoration: 'line-through' }}
                            >
                              {product.currencySymbol}
                              {product.msrpPrice.toFixed(2)}
                            </span>
                          )}
                      </h5>
                    </>
                  )}
                  {callToAction?.text && (
                    <div className="mt-4" data-progress-sku={product.sku}>
                      {actionButton()}
                    </div>
                  )}
                </>
              ) : (
                <PendingMessage type={type} />
              )}
            </div>
          </div>
        </div>
      </div>
      {productModalState && (
        <ProductModal
          accountId={accountId}
          product={product}
          onCancel={(report: boolean = true) => {
            if (report)
              reportLinkClick(
                `${product.name}:Cancel`,
                '',
                'b2bhub_myproducts',
              );
            onCancel();
          }}
          onSubmit={() => {
            reportLinkClick(
              `${name}:Confirm and Proceed`,
              product.callToAction?.url,
              'b2bhub_myproducts',
            );
          }}
          onSuccess={onEnrollmentSuccess}
        />
      )}
      {detailModalState && (
        <DetailModal
          product={product}
          onCancel={(report: boolean = true) => {
            if (report)
              reportLinkClick(
                `${product.name}:Cancel`,
                '',
                'b2bhub_myproducts',
              );
            setDetailModalState(false);
          }}
          onContinue={() => {
            reportLinkClick(
              `${name}:${product.callToAction?.text}`,
              product.callToAction?.url,
              'b2bhub_myproducts',
            );
            const btn: HTMLAnchorElement | null = document.querySelector(
              `[data-progress-sku="${product.sku}"] a`,
            );
            if (btn) {
              btn.click();
            }
            setTimeout(() => {
              setDetailModalState(false);
            }, 300);
          }}
        />
      )}
    </>
  );
}
